import type { AssetDationItem } from '../../../../constants/payment';
import { BRASILIA_TIMEZONE } from '../../../../utils/DateUtils';
import { Auth } from 'aws-amplify';
import appConfig from '../../../../config/app-config';
import { PaymentSourceOption } from './paymentSources';
import { ChargeInvoicesActions } from './chargeInvoicesActions';

const { address: API_URL, key: API_KEY } = appConfig.apis.billing;
const { address: BILLER_URL, key: BILLER_API_KEY } = appConfig.apis.billerApiEnv;

const getHeaders = async () => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'x-api-key': API_KEY,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
    };
};

// ─── Cancelamento (CANCEL_INVOICE) ──────────────────────────────────────────
// Sem boleto: cancela direto. Com boleto: dispara CANCEL_BILL ao biller e o
// invoice só é cancelado quando o BOLETO_CANCELED voltar (assíncrono).

export interface CancelInvoicePayload {
    reason?: string;
    // quem cancelou NÃO vai daqui: o backend grava o usuário logado da própria sessão
    // (billing_action.user_id/user_name). Campo declarado pelo cliente num log de auditoria
    // deixaria o auditado escolher o nome que aparece no histórico.
}

export interface CancelInvoiceResponse {
    invoiceId: string;
    result: 'CANCELLED' | 'CANCEL_BILL_REQUESTED' | 'ALREADY_CANCELLED';
}

export const cancelInvoice = async (invoiceId: number | string, payload: CancelInvoicePayload): Promise<CancelInvoiceResponse> => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/invoices/${invoiceId}/cancel`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const errorBody = await res.json().catch(() => null);
        throw new Error(errorBody?.message || `Erro HTTP ${res.status}`);
    }
    return res.json();
};

// ─── Invoices por operação (modal da tela de Operações) ─────────────────────

export const listInvoicesByOperation = async (creditOperationId: string) => {
    const headers = await getHeaders();
    const params = new URLSearchParams({
        'filter[credit_operation_id]': creditOperationId,
        size: '200',
        sort: 'due_date',
    });
    const res = await fetch(`${API_URL}/invoices?${params.toString()}`, { headers });
    if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);
    return res.json();
};

// ─── Situação do boleto no banco (assíncrono, via biller) ───────────────────

/**
 * Pede ao biller que consulte a situação do boleto no banco (`BILL_LIQUIDATION_CHECK`).
 *
 * ⚠️ ASSÍNCRONO: a resposta é "pedido enviado", nunca "está pago". Quem atualiza a fatura são os
 * `BOLETO_PAID`/`BOLETO_CANCELED` que o biller publica DEPOIS, e só se a situação tiver mudado.
 */
export const checkBoletoLiquidation = async (invoiceRef: string) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/invoices/${invoiceRef}/check-boleto`,
                            { method: 'POST', headers });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Erro HTTP ${res.status}`);
    }
    return res.json();
};

// ─── Cobranças (títulos) em aberto de uma operação ──────────────────────────

/**
 * Charges com dívida ainda viva — a base dos RASCUNHOS de fatura do Registrar Recebimento.
 *
 * É o mesmo endpoint da tela Cobrança — Títulos, e de propósito: ali 1 linha = 1 charge (título),
 * que é a granularidade certa para "quanto ainda se deve nesta cobrança". Listar faturas
 * pré-existentes trazia várias linhas por título e obrigava o usuário a raciocinar sobre frações.
 *
 * O filtro de status é do TÍTULO (a dívida), não da fatura: um título pode estar devendo sem ter
 * fatura aberta nenhuma — é justamente o caso "falta faturar".
 */
export const listOpenChargesByOperation = async (creditOperationId: string) => {
    const headers = await getHeaders();
    const params = new URLSearchParams({
        'filter[credit_operation_id]': creditOperationId,
        'filter[title_debt_status]': 'OPEN,PARTIALLY_PAID',
        size: '200',
        sort: 'due_date',
    });
    const res = await fetch(`${API_URL}/charges?${params.toString()}`, { headers });
    if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);
    return res.json();
};

// ─── Histórico de ações de cobrança (billing_action) ────────────────────────

export const listInvoiceActions = async (invoiceId: number | string) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/invoices/${invoiceId}/actions`, { headers });
    if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);
    return res.json();
};

// ─── Eventos de cobrança da fatura (AF-3664) ────────────────────────────────
// Protesto, Serasa, notificação extrajudicial, pesquisa de bens, sub-rogação. Cada linha é uma
// ação com prazo e dono: nasce PENDENTE e depois alguém a conclui ou cancela.
//
// ⚠️ Não confundir com `listInvoiceActions` acima, que é o log do ATO DE PAGAR (billing_action).

export type BillingEventStatus = 'PENDING' | 'COMPLETED' | 'CANCELLED';

/** Item do catálogo — o TIPO da ação. Só os ativos chegam aqui. */
export interface BillingEventOption {
    id: number;
    name: string;
    aliasName: string;
    isActive: boolean;
}

export interface InvoiceBillingEventRow {
    id: number;
    invoiceId: string;
    billingEventRef: string;
    /** rótulo do catálogo, resolvido no backend — vem MESMO de evento já desativado */
    billingEventName: string | null;
    status: BillingEventStatus;
    /** username de quem cadastrou; o nome pode faltar e a tela cai para ele */
    createdBy: string | null;
    createdByName: string | null;
    finishedBy: string | null;
    finishedByName: string | null;
    completedAt: string | null;
    eventDateLimit: string | null;
    notes: string | null;
    /** comentários já quebrados pelo backend: o separador é regra do domínio, não da tela */
    notesList: string[];
    createdAt: string | null;
}

export interface NewBillingEventPayload {
    billing_event_ref: string;
    /** PENDING (default da tela) ou COMPLETED. CANCELLED não se cadastra. */
    status?: BillingEventStatus;
    notes?: string;
    /** em PENDING é o prazo escolhido; em COMPLETED acompanha a data de criação */
    event_date_limit?: string;
    /** instante ISO da conclusão (só em COMPLETED). Ausente = o backend carimba agora. */
    completed_at?: string;
}

export interface UpdateBillingEventPayload {
    /** COMPLETED ou CANCELLED. Ausente = só comentário. */
    status?: BillingEventStatus;
    /** ACUMULA no backend, nunca sobrescreve. */
    notes?: string;
    /** instante ISO da conclusão. Só vale com status COMPLETED — no cancelamento é ignorado. */
    completed_at?: string;
}

/**
 * Fonte do Pagamento — o que o select da MARCAÇÃO oferece.
 *
 * Vem de `business_properties.PAYMENT_SOURCES` e não de uma constante do front (AF-3671 step 2):
 * o rótulo em português, quem admite aval a devolver e qual é a opção default por natureza do
 * título são regra de negócio, e mudavam só com deploy enquanto viviam em `constants/payment.ts`.
 *
 * A REGRA (qual vem pré-selecionada, quem admite aval) mora em `paymentSources.ts`; aqui fica só a
 * chamada de rede. Reexportadas para quem já importava daqui.
 */
export type { PaymentSourceOption } from './paymentSources';
export { defaultPaymentSource, allowsGuarantorRefund } from './paymentSources';

export const listPaymentSources = async (): Promise<PaymentSourceOption[]> => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/payment-sources`, { headers });
    if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);
    const body = await res.json();
    return body.data || [];
};

/**
 * O modo da fatura residual e o que ele diz à tela (AF-4097): se o elemento "Faturas a serem
 * geradas" aparece, se já vem pré-configurado, com boleto e herdando a cobrança da fatura anterior.
 * Desde o AF-4062 vem de cada combinação de `INVOICE_PAYMENT_ACTION`.
 */
export interface ResidualInvoiceSettings {
    mode: string;
    visible: boolean;
    prefilled: boolean;
    withBoleto: boolean;
    keepsCollection: boolean;
}

/** Uma fatura residual configurada na marcação: o que a aprovação gera com o saldo do título. */
export interface ResidualInvoiceSetup {
    value: number;
    due_date: string | null;
    with_boleto: boolean;
    collect: boolean;
    automatic_preventive_collection: boolean;
    automatic_overdue_collection: boolean;
    bill_charges: Record<string, unknown> | null;
    notes: string | null;
}

/** O que fazer com a fatura anterior, sem o escopo: KEEP é a promessa `NOTHING`. */
export type ParentAction = 'CANCEL' | 'UNCOLLECT' | 'KEEP';

/**
 * Uma combinação (natureza do título × fonte × liquidação) de `INVOICE_PAYMENT_ACTION` já resolvida
 * pelo backend (AF-4062): o padrão da escolha, onde ela aparece e o modo da residual.
 */
export interface PaymentActionResolution {
    parentDefault: ParentAction | null;
    atMarking: boolean;
    atApproval: boolean;
    residual: ResidualInvoiceSettings;
}

export type Liquidation = 'TOTAL' | 'PARTIAL';

/** `resolutions[natureza][fonte][TOTAL|PARTIAL]`. */
export interface InvoicePaymentActionSettings {
    resolutions: Record<string, Record<string, Record<Liquidation, PaymentActionResolution>>>;
}

export const getChargeInvoicesActions = async (): Promise<ChargeInvoicesActions> => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/charge-invoices-actions`, { headers });
    if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);
    const body = await res.json();
    return body.data;
};

export const getInvoicePaymentActionSettings = async (): Promise<InvoicePaymentActionSettings> => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/invoice-payment-action-settings`, { headers });
    if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);
    const body = await res.json();
    return body.data;
};

/** Uma fatura pela chave de negócio, no mesmo formato da listagem (`GET /invoices`). */
export const getInvoiceById = async (invoiceId: string): Promise<any | null> => {
    const headers = await getHeaders();
    const params = new URLSearchParams({ 'filter[invoice_id][eq]': invoiceId, size: '1' });
    const res = await fetch(`${API_URL}/invoices?${params.toString()}`, { headers });
    if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);
    const body = await res.json();
    return (body.items || body.list || body.data || [])[0] || null;
};

export const listBillingEventOptions = async (): Promise<BillingEventOption[]> => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/billing-events`, { headers });
    if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);
    const body = await res.json();
    return body.data || [];
};

export const listInvoiceBillingEvents = async (
    invoiceId: number | string,
): Promise<InvoiceBillingEventRow[]> => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/invoices/${invoiceId}/billing-events`, { headers });
    if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);
    const body = await res.json();
    return body.data || [];
};

/**
 * Cadastra o evento na fatura.
 *
 * ⚠️ Quem cadastrou NÃO vai no payload: o backend grava o usuário da sessão. Num acompanhamento
 * que existe para responder "quem pediu esta ação?", quem é auditado não escolhe o próprio nome
 * — mesmo princípio do CancelInvoicePayload.
 */
export const createInvoiceBillingEvent = async (
    invoiceId: number | string, payload: NewBillingEventPayload,
) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/invoices/${invoiceId}/billing-events`, {
        method: 'POST', headers, body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Erro HTTP ${res.status}`);
    }
    return res.json();
};

/** Concluir, cancelar ou comentar — os três botões da linha caem no mesmo PATCH. */
export const updateInvoiceBillingEvent = async (
    invoiceId: number | string, eventId: number, payload: UpdateBillingEventPayload,
) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/invoices/${invoiceId}/billing-events/${eventId}`, {
        method: 'PATCH', headers, body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Erro HTTP ${res.status}`);
    }
    return res.json();
};

// ─── Pagamento (POST /billing/payments) — AF-3327 ───────────────────────────
// Registra UM payment e dispara os eventos por invoice (REGISTER_INVOICE_PAYMENT /
// REGISTER_PAID_INVOICE + CANCEL_INVOICE/UNCOLLECT_INVOICE).

export interface PaymentPayer {
    external_id?: string;       // uuid do agregado person (quando encontrado no person service)
    name: string;
    document_number: string;
    address_street?: string;
    address_number?: string;
    address_complement?: string;
    address_neighborhood?: string;
    address_city?: string;
    address_state?: string;
    address_zip_code?: string;
    email?: string;
    phone?: string;
}

export interface PaymentInvoiceItem {
    credit_operation_id: string;
    charge_id: string | null;
    invoice_id: string;
    value: number;
    /** tri-state: true liquida o título, false mantém aberto, null = baldinho (só INSTALLMENT
     *  e garantia). O cenário do charge restringe — ver domain/charge_scenario no billing. */
    total_payment: boolean | null;
    cancel_invoice: boolean;
    uncollect: boolean;
    /**
     * Fração do CHARGE que a fatura NOVA cobre. Omitido = herda a da fatura de origem. Declarar
     * importa em pagamento parcial: herdando 1/1 a cobrança parece completa e o charge some do
     * "Falta faturar" mesmo com saldo devedor.
     */
    installment_number?: number;
    total_installments?: number;
    /**
     * Alvo de cancel_invoice/uncollect. Omitido = INVOICE, só a fatura deste item — é o que
     * quer quem age sobre UMA fatura escolhida. `CHARGE` (todas as abertas do título) é do
     * Registrar Recebimento, onde cada linha é um rascunho por título e não uma fatura.
     */
    cancel_scope?: 'INVOICE' | 'CHARGE';
}

export interface ProcessPaymentPayload {
    payment_id?: string;
    amount: number;
    payment_date: string;        // ISO 8601 (instante; o front converte wall-clock local → UTC)
    /** PIX | TRANSFER | BOLETO — só em payment_type=FINANCIAL */
    payment_method?: string;
    payment_type: string;        // FINANCIAL | DEBT_CONFESSION | ASSET_DATION
    payer?: PaymentPayer;
    payer_id?: string;
    /** obrigatório apenas em payment_type=FINANCIAL */
    receiving_bank_account_id?: string;
    user_responsible?: string;
    file_name_receipt?: string;
    file_url_receipt?: string;
    /** Fonte e aval; a sub-rogação saiu (é do TÍTULO — AF-3674) */
    payment_source?: string;
    pending_guarantor_refund?: boolean;
    debt_confession_contract?: string;
    /** UM bem, não uma lista — ver `paymentPayload` e o schema do billing */
    asset_dation?: AssetDationItem | null;
    data?: Record<string, unknown>;
    invoices: PaymentInvoiceItem[];
}

export const processPayment = async (payload: ProcessPaymentPayload) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/payments`, {
        method: 'POST', headers, body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Erro HTTP ${res.status}`);
    }
    return res.json();
};

// ─── Faturas de um charge (drill-in da tela de Cobrança — Títulos) ──────────
// O charge é a dupla (operação, charge_id): filtrar só por charge_id juntaria charges
// homônimos de operações diferentes, que são linhas distintas na tela.

/**
 * UMA página da listagem de faturas, com os filtros e a ordem que a tela já montou.
 *
 * Para a exportação: ela percorre todas as páginas com o MESMO `params` da tela (filtros + ordem),
 * só trocando `page` e `size`. Assim a planilha tem exatamente as linhas que o usuário filtrou, na
 * mesma ordem em que ele as vê.
 *
 * `size` = 500 porque é o teto do servidor (`_page_size` do billing): pedir mais seria cortado em
 * silêncio para 500 e o laço, que confia em `totalPages`, continuaria certo — mas pedir o teto
 * poupa idas e voltas numa exportação grande.
 *
 * `export=true` faz o billing anexar `exportExtras` (e-mail, telefone e PDF do boleto) a cada linha.
 * A tabela não manda o parâmetro e não paga essa consulta.
 */
export const EXPORT_PAGE_SIZE = 500;

export const listInvoicesPage = async (params: URLSearchParams, page: number) => {
    const headers = await getHeaders();
    const query = new URLSearchParams(params);
    query.set('page', String(page));
    query.set('size', String(EXPORT_PAGE_SIZE));
    query.set('export', 'true');
    const res = await fetch(`${API_URL}/invoices?${query.toString().replace(/\+/g, '%20')}`, { headers });
    if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);
    return res.json();
};

/** Uma página de títulos para a exportação: mesma regra do `listInvoicesPage`, em `/charges`. */
export const listChargesPage = async (params: URLSearchParams, page: number) => {
    const headers = await getHeaders();
    const query = new URLSearchParams(params);
    query.set('page', String(page));
    query.set('size', String(EXPORT_PAGE_SIZE));
    const res = await fetch(`${API_URL}/charges?${query.toString().replace(/\+/g, '%20')}`, { headers });
    if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);
    return res.json();
};

/** Faturas do título no modal, uma página por vez (`totalPages` vem na resposta). */
export const CHARGE_INVOICES_PAGE_SIZE = 50;

export const listInvoicesByCharge = async (creditOperationId: string, chargeId: string, page = 1) => {
    const headers = await getHeaders();
    const params = new URLSearchParams({
        'filter[credit_operation_id]': creditOperationId,
        'filter[charge_id]': chargeId,
        page: String(page),
        size: String(CHARGE_INVOICES_PAGE_SIZE),
        sort: 'id',
    });
    const res = await fetch(`${API_URL}/invoices?${params.toString()}`, { headers });
    if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);
    return res.json();
};

// ─── Deixar de cobrar (UNCOLLECT_INVOICE) ───────────────────────────────────
// Para de cobrar SEM cancelar: a fatura continua devida, só sai da cobrança.

export interface UncollectInvoiceResponse {
    invoiceId: string;
    collect: false;
    result: 'UNCOLLECTED' | 'ALREADY_UNCOLLECTED';
}

export interface CollectInvoiceResponse {
    invoiceId: string;
    collect: true;
    result: 'COLLECTING' | 'ALREADY_COLLECTING';
}

/** Volta a cobrar uma fatura que tinha saído da cobrança — inverso do uncollect.
 *  Quem retomou é gravado no billing_action a partir da sessão, no backend. */
export const collectInvoice = async (invoiceId: number | string): Promise<CollectInvoiceResponse> => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/invoices/${invoiceId}/collect`, { method: 'POST', headers });
    if (!res.ok) {
        const errorBody = await res.json().catch(() => null);
        throw new Error(errorBody?.message || `Erro HTTP ${res.status}`);
    }
    return res.json();
};

/** Para de cobrar a fatura. O motivo é obrigatório: o billing o grava como motivo da suspensão
 *  (`collectionSuspendedReason`), que a cobrança lê depois (AF-3940). */
export const uncollectInvoice = async (
    invoiceId: number | string,
    reason: string,
): Promise<UncollectInvoiceResponse> => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/invoices/${invoiceId}/uncollect`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason }),
    });
    if (!res.ok) {
        const errorBody = await res.json().catch(() => null);
        throw new Error(errorBody?.message || `Erro HTTP ${res.status}`);
    }
    return res.json();
};

// ─── Gerar nova fatura a partir de outra (POST /invoices/<ref>/derive) ──────
// A fração de cada fatura é DERIVADA da quantidade enviada: o conjunto fecha o charge
// por construção. Cancelar/descobrar valem só para a fatura de origem.

export interface DeriveInvoiceItem {
    value: number;
    due_date?: string | null;
    with_boleto?: boolean;
    collect?: boolean | null;
    /** "Automações": a régua pode cobrar antes e depois do vencimento. Carimbadas na criação,
     *  imutáveis (AF-3940). Só fazem sentido com `collect`. */
    automatic_preventive_collection?: boolean | null;
    automatic_overdue_collection?: boolean | null;
    /** tri-state: true liquida, false mantém aberto, null = baldinho (só INSTALLMENT) */
    total_payment?: boolean | null;
    document_title?: string | null;
    notes?: string | null;
    bill_charges?: Record<string, unknown> | null;
}

export interface DeriveInvoicesPayload {
    invoices: DeriveInvoiceItem[];
    cancel_parent?: boolean;
    uncollect_parent?: boolean;
    reason?: string;
}

export const deriveInvoices = async (parentInvoiceId: string, payload: DeriveInvoicesPayload) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/invoices/${parentInvoiceId}/derive`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const errorBody = await res.json().catch(() => null);
        throw new Error(errorBody?.message || `Erro HTTP ${res.status}`);
    }
    return res.json();
};

// ─── Tela de Pagamentos (o ato de pagar em 3 passos) ────────────────────────

/** Estado de identificação — DERIVADO das faturas ligadas, não é coluna do banco. */
export type PaymentStatus = 'PENDING' | 'MARKED' | 'IDENTIFIED';

export interface PaymentRow {
    paymentId: string;
    amount: number | null;
    paymentDate: string | null;
    paymentMethod: string | null;   // null fora de FINANCIAL — não é falta de dado
    paymentType: string;
    payer: { name: string; documentNumber: string } | null;
    receivingBankAccount: { exibitionName: string; aliasName?: string } | null;
    /** o pagamento entrou numa conta Escrow da conta de recebimento (AF-4155) */
    escrowAccount?: boolean;
    fileUrlReceipt: string | null;
    fileNameReceipt: string | null;
    userResponsible: string | null;
    /** observação de quem REGISTROU o pagamento (opcional) — não a da cobrança da fatura */
    notes: string | null;
    /** as observações já quebradas pelo separador do domínio (a mais recente por último) */
    notesList?: string[];
    /** o acontecimento mais recente das marcações (MARKED, APPROVED...); null sem histórico */
    lastMarkingEvent?: string | null;
    /**
     * O RESUMO da repartição abaixo, não um estado do pagamento.
     *
     * Desde o step 4 um pagamento pode estar nos três passos ao mesmo tempo. Este campo responde
     * "ainda sobra o que marcar?" — é o que o filtro das listas de escolha usa —, e quem precisa
     * PINTAR ou desabilitar botão usa as partes.
     */
    status: PaymentStatus;
    linkedInvoicesCount: number;
    /** agendado + efetivado: "quanto deste pagamento já tem destino" */
    markedAmount: number;
    /** marcado e ainda não efetivado — o âmbar da linha */
    scheduledAmount: number;
    /** já efetivado (carimbado na fatura) — o verde da linha */
    paidAmount: number;
    /** o que ainda falta marcar — a coluna "Pendente" das listas de escolha */
    pendingAmount: number;
    scheduledCount: number;
    paidCount: number;
    createdAt: string | null;
}

/**
 * Pagamentos de UM dia.
 *
 * O fuso vai junto porque `payment_date` é timestamptz em UTC: sem ele, um PIX das 22h em São
 * Paulo cai no fechamento do dia seguinte.
 */
export const listPayments = async (date: string, timeZone?: string): Promise<PaymentRow[]> => {
    const headers = await getHeaders();
    const params = new URLSearchParams({ date });
    params.set('tz', timeZone || BRASILIA_TIMEZONE);
    const res = await fetch(`${API_URL}/payments?${params}`, { headers });
    if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);
    const body = await res.json();
    return body.list || [];
};

/**
 * A FILA de pagamentos num estado, sem recorte de dia.
 *
 * Para quem MARCA faturas (passo 2), o dia não é o recorte útil: dinheiro que entrou na terça é
 * identificado na quarta. O servidor limita a janela — pendente muito antigo é caso de
 * conciliação, não item de fila.
 */
export const listPaymentsByStatus = async (
    status: PaymentStatus, timeZone?: string,
): Promise<PaymentRow[]> => {
    const headers = await getHeaders();
    const params = new URLSearchParams({ status });
    params.set('tz', timeZone || BRASILIA_TIMEZONE);
    const res = await fetch(`${API_URL}/payments?${params}`, { headers });
    if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);
    const body = await res.json();
    return body.list || [];
};

/** Passo 1: só a entrada do dinheiro. Sem fatura — quem identifica é a cobrança, depois. */
export const registerPayment = async (payload: Record<string, any>) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/payments/register`, {
        method: 'POST', headers, body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Erro HTTP ${res.status}`);
    }
    return res.json();
};

/**
 * Faturas marcadas em UM pagamento — drill-in da tela de Pagamentos.
 *
 * ⚠️ Do BILLING, não do settlement. As duas APIs têm um `/invoices`, e são coisas diferentes: a
 * fatura e o `paid_by` que a liga ao pagamento vivem aqui. Apontar para o settlement devolvia uma
 * lista alheia, idêntica para qualquer pagamento.
 */
export const listInvoicesByPayment = async (paymentId: string) => listInvoicesByPayments([paymentId]);

/**
 * Faturas de VÁRIOS pagamentos (AF-4139): o modal abre a seleção inteira, e soma, seleciona e aprova
 * todas — por isso busca todas as páginas, no tamanho máximo que a API aceita.
 *
 * Os ids vão em lotes: com a seleção de várias páginas, uma consulta só passava do limite de URL do
 * API Gateway (8.192 caracteres) e o modal abria vazio. Uma fatura ligada a dois pagamentos de lotes
 * diferentes volta nos dois, e entra uma vez.
 */
const INVOICES_PAGE_SIZE = 500;
const PAYMENTS_PER_QUERY = 50;

const listInvoicesOfBatch = async (paymentIds: string[], headers: HeadersInit): Promise<any[]> => {
    const invoices: any[] = [];
    for (let page = 1; ; page++) {
        const params = new URLSearchParams({
            'filter[paid_by]': paymentIds.join(','), page: String(page), size: String(INVOICES_PAGE_SIZE),
        });
        const res = await fetch(`${API_URL}/invoices?${params}`, { headers });
        if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);
        const body = await res.json();
        invoices.push(...(body.list || []));
        if (page >= (body.totalPages || 1)) return invoices;
    }
};

export const listInvoicesByPayments = async (paymentIds: string[]): Promise<any[]> => {
    const headers = await getHeaders();
    const batches: string[][] = [];
    for (let start = 0; start < paymentIds.length; start += PAYMENTS_PER_QUERY) {
        batches.push(paymentIds.slice(start, start + PAYMENTS_PER_QUERY));
    }
    const results = await Promise.all(batches.map(batch => listInvoicesOfBatch(batch, headers)));
    const unique = new Map<string, any>(results.flat().map(invoice => [invoice.invoiceId, invoice]));
    return Array.from(unique.values());
};

export type PaymentEditableField =
    'amount' | 'payment_date' | 'payment_type' | 'payment_method' | 'receiving_bank_account_id'
    | 'payer_id' | 'notes' | 'data' | 'escrow_account';

export interface PaymentDetailPayer {
    personId: string | null;
    externalId: string | null;
    name: string | null;
    documentNumber: string | null;
    addressStreet: string | null;
    addressNumber: string | null;
    addressComplement: string | null;
    addressNeighborhood: string | null;
    addressCity: string | null;
    addressState: string | null;
    addressZipCode: string | null;
    email: string | null;
    phone: string | null;
}

export interface PaymentDetail {
    paymentId: string;
    amount: number;
    paymentDate: string | null;
    paymentType: string;
    paymentMethod: string | null;
    receivingBankAccount: {
        receivingBankAccountId: string;
        aliasName: string;
        exibitionName: string;
        active: boolean;
        escrowAccountSupport?: boolean;
    } | null;
    escrowAccount?: boolean;
    payer: PaymentDetailPayer | null;
    notes: string | null;
    notesList?: string[];
    assetDation: AssetDationItem | null;
    debtConfessionContract: string | null;
    effectedAmount: number;
    markedAmount: number;
    /** efetivado + sinalizado: o valor não pode ficar abaixo disso */
    minAmount: number;
    /** só o valor com parte efetivada; boleto: só data, observação e pagador */
    editableFields: PaymentEditableField[];
}

export interface MarkingHistoryEntry {
    historyId: string;
    paymentSchedulingId: string;
    paymentId: string;
    event: string;
    eventFormatted: string;
    eventNotes: string | null;
    occurredAt: string | null;
    actor: { id: string; name: string | null } | null;
    effectedInvoiceId: string | null;
    invoice: {
        invoiceId: string;
        documentTitle: string | null;
        dueDate: string | null;
        amount: number | null;
        installmentNumber: number | null;
        totalInstallments: number | null;
        payer: { name: string | null; documentNumber: string | null } | null;
    };
    marking: {
        amount: number | null;
        paymentActionPromise: string | null;
        paymentActionScope?: string | null;
        totalPayment: boolean | null;
        installmentNumber: number | null;
        totalInstallments: number | null;
        paymentSource: string | null;
        pendingGuarantorRefund: boolean;
        notesList: string[];
        markedBy: string | null;
        markedByName: string | null;
        markedAt: string | null;
    };
}

/** O histórico das marcações do pagamento, do acontecimento mais antigo para o mais novo. */
export const getPaymentMarkingHistory = async (paymentId: string): Promise<MarkingHistoryEntry[]> => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/payments/${paymentId}/marking-history`, { headers });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Erro HTTP ${res.status}`);
    }
    const body = await res.json();
    return body.data || [];
};

const postNote = async (url: string, notes: string) => {
    const headers = await getHeaders();
    const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify({ notes }) });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Erro HTTP ${res.status}`);
    }
    return res.json();
};

/** Anotação de PAGAMENTO da fatura (`invoice.payment_notes`) — ACUMULA, separada dos comentários. */
export const addInvoicePaymentNote = (invoiceRef: string, notes: string) =>
    postNote(`${API_URL}/invoices/${invoiceRef}/payment-notes`, notes);

/** Observação na marcação viva — ACUMULA; na aprovação vai para o `payment_notes` da fatura. */
export const addMarkingNote = (paymentSchedulingId: string, notes: string) =>
    postNote(`${API_URL}/payment-schedulings/${paymentSchedulingId}/notes`, notes);

/** Mensagem do PAGAMENTO (`payment.notes`): acumula e vale em qualquer estado do pagamento. */
export const addPaymentNote = (paymentRef: string, notes: string) =>
    postNote(`${API_URL}/payments/${paymentRef}/notes`, notes);

export const getPayment = async (paymentId: string): Promise<PaymentDetail> => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/payments/${paymentId}`, { headers });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Erro HTTP ${res.status}`);
    }
    const body = await res.json();
    return body.data;
};

export interface UpdatePaymentPayer {
    name: string;
    document_number: string;
    external_id?: string | null;
    address_street?: string | null;
    address_number?: string | null;
    address_complement?: string | null;
    address_neighborhood?: string | null;
    address_city?: string | null;
    address_state?: string | null;
    address_zip_code?: string | null;
    email?: string | null;
    phone?: string | null;
}

/** Só os campos presentes mudam. Com parte já efetivada o servidor aceita apenas `amount`. */
export interface UpdatePaymentPayload {
    amount?: number;
    payment_date?: string;
    payment_type?: string;
    payment_method?: string | null;
    receiving_bank_account_id?: string | null;
    escrow_account?: boolean;
    payer?: UpdatePaymentPayer;
    notes?: string | null;
    asset_dation?: AssetDationItem | null;
    debt_confession_contract?: string | null;
}

export const updatePayment = async (
    paymentId: string, payload: UpdatePaymentPayload,
): Promise<{ payment_id: string; changed: string[] }> => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/payments/${paymentId}`, {
        method: 'PUT', headers, body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Erro HTTP ${res.status}`);
    }
    const body = await res.json();
    return body.data;
};

/**
 * Exclui o pagamento — só antes da efetivação.
 *
 * Faturas apenas sinalizadas perdem o vínculo (`paid_by` e `amount_paid` voltam a nulo) e seguem
 * abertas. Pagamento já IDENTIFICADO é recusado pelo servidor: ali as faturas estão pagas e o
 * settlement já movimentou os títulos.
 */
export const deletePayment = async (paymentId: string) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/payments/${paymentId}`, { method: 'DELETE', headers });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Erro HTTP ${res.status}`);
    }
    return res.json();
};

/** O que fazer com a fatura ANTERIOR quando o pagamento for aprovado (promessa diferida). */
export type PaymentActionPromise =
    'NOTHING' | 'CANCEL' | 'CANCEL_ALL' | 'UNCOLLECT' | 'UNCOLLECT_ALL';

/** Onde a promessa age: a fatura anterior (tela de Títulos) ou as abertas do título (step 3). */
export type PromiseScope = 'INVOICE' | 'CHARGE';

export interface MarkInvoiceItem {
    invoice_id: string;
    value: number;
    /**
     * `NOTHING` é como se diz "nada acontece com a anterior". O sufixo `_ALL` troca o ALVO (todas
     * as faturas abertas do título), não a intensidade. Ausente (AF-4062): o backend resolve pela
     * combinação de `INVOICE_PAYMENT_ACTION` — nula para o aprovador, o padrão quando o campo está
     * oculto —, e então `payment_action_scope` é obrigatório.
     */
    payment_action_promise?: PaymentActionPromise;
    payment_action_scope?: PromiseScope;
    /** tri-state: ausente herda o modo da fatura; `null` explícito é o baldinho. */
    total_payment?: boolean | null;
    installment_number?: number | null;
    total_installments?: number | null;
    /**
     * Atributos da COBRANÇA (AF-3671 step 2), informados aqui e carimbados na fatura pela
     * efetivação. Saíram do registro do pagamento: quem entrega o dinheiro não sabe a qual
     * cobrança ele pertence.
     */
    payment_source?: string | null;
    /** só faz sentido com uma fonte que admite devolução — ver `canGuaranteeRefund` */
    pending_guarantor_refund?: boolean | null;
    notes?: string | null;
    /**
     * Faturas residuais configuradas (AF-4097), só no pagamento parcial. Ausente = não configurada
     * (vale o modo da combinação); `[]` = configurada para não gerar nada.
     */
    residual_invoices?: ResidualInvoiceSetup[];
}

/**
 * Passo 2: diz quais faturas este pagamento paga — SEM liquidá-las.
 *
 * As faturas ficam abertas com o pagamento apontado; a liquidação depende da aprovação do passo 3.
 * O servidor recusa pagamento fora de PENDENTE, título que já tem fatura marcada em outro
 * pagamento, e soma que passe do valor recebido.
 */
export interface MarkOptions {
    /**
     * Marcações VIVAS a liberar ANTES de gravar as novas, no mesmo ato.
     *
     * É o que faz a seção "Já Marcado" funcionar: remover manda o id aqui, e EDITAR manda o id aqui
     * e o item em `invoices` — substituir é liberar e marcar de novo. Vai no mesmo corpo de
     * propósito: em duas chamadas, uma falha na segunda deixaria a marcação antiga apagada e a nova
     * nunca gravada.
     */
    release?: string[];
    releaseReason?: string;
}

export const markInvoices = async (
    paymentId: string, invoices: MarkInvoiceItem[], options: MarkOptions = {},
) => {
    const headers = await getHeaders();
    const payload: Record<string, any> = { invoices };
    if (options.release?.length) payload.release = options.release;
    if (options.releaseReason) payload.release_reason = options.releaseReason;
    const res = await fetch(`${API_URL}/payments/${paymentId}/mark`, {
        method: 'POST', headers, body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Erro HTTP ${res.status}`);
    }
    return res.json();
};

/**
 * Remove a sinalização, com motivo obrigatório.
 *
 * Sem `invoices`, age no pagamento INTEIRO — é o que a "Reprovar Identificação" faz, e quem chama
 * precisa avisar o usuário quando há mais de uma fatura no lote. Com a lista, remove só aquelas: a
 * conta aberta deixou de ser um estado quebrado quando a marcação parcial passou a existir.
 *
 * ⚠️ O que já foi EFETIVADO não é alcançado aqui, e não por uma checagem: a efetivação apaga a
 * marcação, e este endpoint só mexe em marcação viva.
 */
export const unmarkInvoices = async (
    paymentId: string, reason: string, invoices?: string[],
) => {
    const headers = await getHeaders();
    const payload: Record<string, any> = { reason };
    if (invoices?.length) payload.invoices = invoices;
    const res = await fetch(`${API_URL}/payments/${paymentId}/unmark`, {
        method: 'POST', headers, body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Erro HTTP ${res.status}`);
    }
    return res.json();
};

/**
 * Passo 3: o financeiro efetiva o que a cobrança marcou.
 *
 * Sem `invoices`, efetiva TUDO o que está marcado. Com a lista, só aquelas linhas — é o aprovar por
 * linha do modal de faturas.
 *
 * ⚠️ NÃO exige mais que o pagamento esteja fechado. Efetivar 30% é legítimo: o pagamento segue
 * pendente e a cobrança continua marcando o resto.
 */
/** A escolha do aprovador para a fatura anterior; `parent_action` nulo = sem escolha (AF-4062). */
export interface ApprovalChoice {
    invoice_id: string;
    parent_action: ParentAction | null;
}

export const approveIdentification = async (
    paymentId: string, invoices?: (string | ApprovalChoice)[],
) => {
    const headers = await getHeaders();
    const payload: Record<string, any> = {};
    if (invoices?.length) payload.invoices = invoices;
    const res = await fetch(`${API_URL}/payments/${paymentId}/approve-identification`, {
        method: 'POST', headers, body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Erro HTTP ${res.status}`);
    }
    return res.json();
};

// ─── Download do PDF do boleto (via biller) ─────────────────────────────────

/**
 * Pede ao biller um link temporário do PDF e devolve a URL.
 *
 * Vai DIRETO ao biller (não passa pelo billing): o billing é event-driven e não faz chamada HTTP a
 * outro serviço — abrir essa exceção só para um link seria um padrão novo lá. As telas do menu
 * Financeiro já consomem o biller assim.
 *
 * `format=json` é obrigatório: sem ele o endpoint responde 302 para o S3, e aí o `fetch` segue o
 * redirect sozinho, cai num bucket sem CORS e ainda perde o corpo. Com a URL em mãos, quem abre é
 * uma navegação (`window.open`), que não passa por CORS.
 *
 * @param billRef correlation_id (UUID) ou nosso número (até 8 dígitos)
 */
export const getBoletoDownloadUrl = async (billRef: string): Promise<string> => {
    const res = await fetch(`${BILLER_URL}/download/${billRef}?format=json`, {
        method: 'GET',
        headers: { 'x-api-key': BILLER_API_KEY },
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Erro HTTP ${res.status}`);
    }
    const { url } = await res.json();
    if (!url) throw new Error('O biller não devolveu link de download');
    return url;
};

// ─── Comprovante de pagamento ───────────────────────────────────────────────

/**
 * Sobe o PDF do comprovante e devolve o nome canônico com que ele ficou guardado.
 *
 * Vai ANTES de registrar o pagamento, de propósito: `POST /payments/register` é assíncrono
 * (publica REGISTER_PAYMENT e quem cria a linha é o invoice_manager), então na hora do save o
 * pagamento ainda não existe — e um PDF não caberia no evento. O nome devolvido aqui viaja em
 * `file_name_receipt` no comando de registro.
 *
 * O arquivo sobe em base64 pelo lambda, e não por PUT direto no S3, porque o bucket não tem CORS.
 */
export const uploadPaymentReceipt = async (
    paymentId: string,
    file: File,
    paymentDate: string,
    payerName?: string | null,
): Promise<{ fileName: string; fileUrl: string }> => {
    const headers = await getHeaders();
    const content = await fileToBase64(file);
    const res = await fetch(`${API_URL}/payments/${paymentId}/receipt`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ content, payment_date: paymentDate, payer_name: payerName || null }),
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Erro HTTP ${res.status}`);
    }
    const { data } = await res.json();
    return data;
};

/** Lê o arquivo como base64 puro (sem o prefixo `data:...;base64,`). */
const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = String(reader.result || '');
            resolve(result.includes(',') ? result.split(',')[1] : result);
        };
        reader.onerror = () => reject(new Error('Não foi possível ler o arquivo'));
        reader.readAsDataURL(file);
    });

/**
 * Link temporário do comprovante. Quem abre é uma navegação (`window.open`), não um fetch: o
 * bucket não tem CORS, então buscar o PDF por fetch seria barrado.
 */
export const getPaymentReceiptUrl = async (paymentId: string): Promise<string> => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/payments/${paymentId}/receipt`, { method: 'GET', headers });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Erro HTTP ${res.status}`);
    }
    const { data } = await res.json();
    if (!data?.url) throw new Error('O backend não devolveu link do comprovante');
    return data.url;
};


// ─── Comentários da fatura ──────────────────────────────────────────────────

/**
 * Acrescenta um comentário à fatura — ACUMULA, nunca substitui.
 *
 * Mesmo comportamento do comentário em evento de cobrança, e pelo mesmo motivo: a fatura passa por
 * várias mãos e trocar o texto apagaria o histórico de quem escreveu antes. A tela mostra o último
 * na coluna "Comentários" e guarda todos no tooltip.
 */
export const addInvoiceComment = async (invoiceRef: string | number, notes: string) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/invoices/${invoiceRef}/comments`, {
        method: 'POST', headers, body: JSON.stringify({ notes }),
    });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Erro HTTP ${res.status}`);
    }
    return res.json();
};

// ─── Marcadores do calendário da tela de Pagamentos ─────────────────────────

export interface PaymentCalendarDay {
    /** YYYY-MM-DD no fuso de quem olha */
    date: string;
    count: number;
    pendingAmount: number;
    markedAmount: number;
    identifiedAmount: number;
}

/**
 * Quais DIAS têm pagamento, e em que estado.
 *
 * Escolher o dia da tela era às cegas: o seletor abria sem pista de onde havia dinheiro para
 * trabalhar. Isto só pinta o calendário — não filtra a lista.
 *
 * ⚠️ O servidor limita a janela (~3 meses). A tela pede um mês por vez.
 */
export const listPaymentsCalendar = async (
    from: string, to: string, timeZone?: string,
): Promise<PaymentCalendarDay[]> => {
    const headers = await getHeaders();
    const params = new URLSearchParams({ from, to });
    params.set('tz', timeZone || BRASILIA_TIMEZONE);
    const res = await fetch(`${API_URL}/payments/calendar?${params}`, { headers });
    if (!res.ok) throw new Error(`Erro HTTP ${res.status}`);
    const body = await res.json();
    return body.days || [];
};
